/**
 * ChatPanel State Management
 *
 * Centralized state management for the chat panel.
 * Manages messages, loading states, and UI state transitions.
 *
 * This module extracts state management from panel.ts to reduce its size
 * and improve maintainability while preserving all functionality.
 */

import type { ChatMessage } from '../../core/config';
// Note: detectedLanguage uses string (not SupportedLanguage) because the backend
// language instruction supports more languages than the widget UI translations.

// =============================================================================
// CONSTANTS
// =============================================================================

/** LocalStorage key for persisting speed mode preference */
const SPEED_MODE_STORAGE_KEY = 'pms-speed-mode';

/** SessionStorage key prefix for persisting conversation across page navigations */
const CONVERSATION_STORAGE_PREFIX = 'pms-conversation-';

/** Max age (ms) for restored conversations — discard if older than 30 minutes */
const CONVERSATION_MAX_AGE_MS = 30 * 60 * 1000;

/**
 * Serializable conversation snapshot for sessionStorage persistence.
 * Stored per workspace so multiple widgets don't collide.
 */
interface ConversationSnapshot {
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    blocks?: unknown[];
    timestamp: number; // epoch ms (Date is not JSON-serializable)
  }>;
  isVisible: boolean;
  detectedLanguage: string | null;
  savedAt: number;
}

/**
 * Panel UI state - mirrors all state variables from ChatPanel
 */
export interface PanelState {
  /** All messages in the conversation */
  messages: ChatMessage[];
  /** Whether a request is in progress */
  isLoading: boolean;
  /** Speed mode enabled (faster, shorter responses) */
  speedMode: boolean;
  /** Whether streaming is enabled */
  useStreaming: boolean;
  /** Message waiting for API key before sending */
  pendingMessage: string | null;
  /** Whether the panel is currently visible (has open class) */
  isVisible: boolean;
  /** Whether the immersive menu dropdown is open */
  isMenuOpen: boolean;
  /** Detected user language for multilingual responses */
  detectedLanguage: string | null;
  /** Last action timestamp for click-spam protection */
  lastActionTime: number;
}

/**
 * State change listener callback
 */
export type StateListener = (state: PanelState, changedKeys: (keyof PanelState)[]) => void;

/**
 * Create initial state with optional overrides
 */
export function createInitialState(overrides?: Partial<PanelState>): PanelState {
  return {
    messages: [],
    isLoading: false,
    speedMode: false,
    useStreaming: true,
    pendingMessage: null,
    isVisible: false,
    isMenuOpen: false,
    detectedLanguage: null,
    lastActionTime: 0,
    ...overrides,
  };
}

/**
 * ChatPanel state manager
 *
 * Provides a centralized, observable state container for the chat panel.
 * Components can subscribe to state changes and react accordingly.
 */
export class ChatPanelState {
  private state: PanelState;
  private listeners: Set<StateListener> = new Set();

  /** Temporary booking slot data from calendar selection, consumed by flow submit */
  bookingSlotData: { start: string; end: string; locationId?: string; meetingTypeId?: string; partySize?: number } | null = null;

  constructor(initialState?: Partial<PanelState>) {
    this.state = createInitialState(initialState);
  }

  // ==========================================================================
  // CORE STATE ACCESS
  // ==========================================================================

  /**
   * Get current state (read-only snapshot).
   * Returns a shallow copy with messages array also copied for immutability.
   */
  getState(): Readonly<PanelState> {
    return { ...this.state, messages: [...this.state.messages] };
  }

  /**
   * Get a specific state value
   */
  get<K extends keyof PanelState>(key: K): PanelState[K] {
    return this.state[key];
  }

  /**
   * Update state with partial changes
   */
  setState(updates: Partial<PanelState>): void {
    const changedKeys: (keyof PanelState)[] = [];

    for (const key of Object.keys(updates) as (keyof PanelState)[]) {
      if (this.state[key] !== updates[key]) {
        changedKeys.push(key);
      }
    }

    if (changedKeys.length === 0) return;

    this.state = { ...this.state, ...updates };
    this.notifyListeners(changedKeys);
  }

  /**
   * Subscribe to state changes
   * @returns Unsubscribe function
   */
  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of state changes.
   * Creates a copy of listeners to prevent issues if a listener modifies the set.
   */
  private notifyListeners(changedKeys: (keyof PanelState)[]): void {
    const snapshot = this.getState();
    // Copy to prevent modification during iteration
    const listeners = [...this.listeners];
    for (const listener of listeners) {
      try {
        listener(snapshot, changedKeys);
      } catch (error) {
        console.error('State listener error:', error);
      }
    }
  }

  // ==========================================================================
  // MESSAGE OPERATIONS
  // ==========================================================================

  /**
   * Add a message to the conversation
   */
  addMessage(message: ChatMessage): void {
    this.state.messages = [...this.state.messages, message];
    this.notifyListeners(['messages']);
  }

  /**
   * Update an existing message by ID
   */
  updateMessage(id: string, updates: Partial<ChatMessage>): void {
    const index = this.state.messages.findIndex(m => m.id === id);
    if (index === -1) return;

    const updatedMessages = [...this.state.messages];
    updatedMessages[index] = { ...updatedMessages[index], ...updates } as ChatMessage;
    this.state.messages = updatedMessages;
    this.notifyListeners(['messages']);
  }

  /**
   * Get all messages (read-only)
   */
  getMessages(): readonly ChatMessage[] {
    return this.state.messages;
  }

  /**
   * Get the last message
   */
  getLastMessage(): ChatMessage | undefined {
    return this.state.messages[this.state.messages.length - 1];
  }

  /**
   * Clear all messages
   */
  clearMessages(): void {
    if (this.state.messages.length === 0) return;
    this.state.messages = [];
    this.notifyListeners(['messages']);
  }

  // ==========================================================================
  // LOADING STATE
  // ==========================================================================

  /**
   * Set loading state
   */
  setLoading(isLoading: boolean): void {
    if (this.state.isLoading === isLoading) return;
    this.state.isLoading = isLoading;
    this.notifyListeners(['isLoading']);
  }

  /**
   * Check if currently loading
   */
  isCurrentlyLoading(): boolean {
    return this.state.isLoading;
  }

  // ==========================================================================
  // VISIBILITY STATE
  // ==========================================================================

  /**
   * Set panel visibility
   */
  setVisible(isVisible: boolean): void {
    if (this.state.isVisible === isVisible) return;
    this.state.isVisible = isVisible;
    this.notifyListeners(['isVisible']);
  }

  /**
   * Check if panel is visible
   */
  isCurrentlyVisible(): boolean {
    return this.state.isVisible;
  }

  // ==========================================================================
  // SPEED MODE
  // ==========================================================================

  /**
   * Toggle speed mode and persist to localStorage.
   * Silently fails if localStorage is unavailable (e.g., private browsing).
   */
  toggleSpeedMode(): boolean {
    this.state.speedMode = !this.state.speedMode;
    try {
      localStorage.setItem(SPEED_MODE_STORAGE_KEY, String(this.state.speedMode));
    } catch {
      // Silently fail - speed mode just won't persist
    }
    this.notifyListeners(['speedMode']);
    return this.state.speedMode;
  }

  /**
   * Get speed mode state
   */
  isSpeedModeEnabled(): boolean {
    return this.state.speedMode;
  }

  /**
   * Load speed mode from localStorage.
   * Silently fails if localStorage is unavailable.
   */
  loadSpeedModePreference(): void {
    try {
      const saved = localStorage.getItem(SPEED_MODE_STORAGE_KEY);
      if (saved !== null) {
        this.state.speedMode = saved === 'true';
      }
    } catch {
      // Silently fail - use default value
    }
  }

  // ==========================================================================
  // STREAMING MODE
  // ==========================================================================

  /**
   * Toggle streaming mode
   */
  toggleStreaming(): boolean {
    this.state.useStreaming = !this.state.useStreaming;
    this.notifyListeners(['useStreaming']);
    return this.state.useStreaming;
  }

  /**
   * Check if streaming is enabled
   */
  isStreamingEnabled(): boolean {
    return this.state.useStreaming;
  }

  // ==========================================================================
  // PENDING MESSAGE (for API key flow)
  // ==========================================================================

  /**
   * Set a pending message (waiting for API key)
   */
  setPendingMessage(message: string | null): void {
    if (this.state.pendingMessage === message) return;
    this.state.pendingMessage = message;
    this.notifyListeners(['pendingMessage']);
  }

  /**
   * Get and clear the pending message
   */
  consumePendingMessage(): string | null {
    const message = this.state.pendingMessage;
    if (message) {
      this.state.pendingMessage = null;
      this.notifyListeners(['pendingMessage']);
    }
    return message;
  }

  /**
   * Check if there's a pending message
   */
  hasPendingMessage(): boolean {
    return this.state.pendingMessage !== null;
  }

  // ==========================================================================
  // MENU STATE
  // ==========================================================================

  /**
   * Toggle immersive menu
   */
  toggleMenu(): boolean {
    this.state.isMenuOpen = !this.state.isMenuOpen;
    this.notifyListeners(['isMenuOpen']);
    return this.state.isMenuOpen;
  }

  /**
   * Close immersive menu
   */
  closeMenu(): void {
    if (!this.state.isMenuOpen) return;
    this.state.isMenuOpen = false;
    this.notifyListeners(['isMenuOpen']);
  }

  /**
   * Check if menu is open
   */
  isMenuCurrentlyOpen(): boolean {
    return this.state.isMenuOpen;
  }

  // ==========================================================================
  // LANGUAGE DETECTION
  // ==========================================================================

  /**
   * Set detected language
   */
  setDetectedLanguage(language: string | null): void {
    if (this.state.detectedLanguage === language) return;
    this.state.detectedLanguage = language;
    this.notifyListeners(['detectedLanguage']);
  }

  /**
   * Get detected language
   */
  getDetectedLanguage(): string | null {
    return this.state.detectedLanguage;
  }

  // ==========================================================================
  // CLICK SPAM PROTECTION
  // ==========================================================================

  /**
   * Check if enough time has passed since last action (for spam protection)
   * @param debounceMs Minimum milliseconds between actions
   * @returns true if action should be allowed
   */
  canPerformAction(debounceMs: number): boolean {
    const now = Date.now();
    if (now - this.state.lastActionTime < debounceMs) {
      return false;
    }
    this.state.lastActionTime = now;
    // Don't notify listeners for this - it's internal tracking
    return true;
  }

  /**
   * Record that an action was performed
   */
  recordAction(): void {
    this.state.lastActionTime = Date.now();
  }

  // ==========================================================================
  // CONVERSATION PERSISTENCE (across page navigations)
  // ==========================================================================

  /**
   * Save current conversation to sessionStorage so it survives page navigations.
   * Called automatically on addMessage and before navigation.
   */
  saveConversation(workspaceId: string): void {
    if (this.state.messages.length === 0) return;

    const snapshot: ConversationSnapshot = {
      messages: this.state.messages.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        blocks: m.blocks,
        timestamp: m.timestamp instanceof Date ? m.timestamp.getTime() : Number(m.timestamp),
      })),
      isVisible: this.state.isVisible,
      detectedLanguage: this.state.detectedLanguage,
      savedAt: Date.now(),
    };

    try {
      sessionStorage.setItem(
        CONVERSATION_STORAGE_PREFIX + workspaceId,
        JSON.stringify(snapshot)
      );
    } catch {
      // Quota exceeded or unavailable — silent fail
    }
  }

  /**
   * Restore conversation from sessionStorage after a page navigation.
   * Returns the restored messages (empty array if nothing to restore).
   * Clears the stored snapshot after restoring to avoid stale replays.
   */
  restoreConversation(workspaceId: string): ChatMessage[] {
    const key = CONVERSATION_STORAGE_PREFIX + workspaceId;
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return [];

      const snapshot = JSON.parse(raw) as ConversationSnapshot;

      // Discard stale conversations
      if (Date.now() - snapshot.savedAt > CONVERSATION_MAX_AGE_MS) {
        sessionStorage.removeItem(key);
        return [];
      }

      // Rehydrate messages (convert epoch ms back to Date)
      const messages: ChatMessage[] = snapshot.messages.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        blocks: m.blocks as ChatMessage['blocks'],
        timestamp: new Date(m.timestamp),
      }));

      // Restore state
      this.state.messages = messages;
      this.state.isVisible = snapshot.isVisible;
      if (snapshot.detectedLanguage) {
        this.state.detectedLanguage = snapshot.detectedLanguage;
      }

      // Clear stored snapshot — it's been consumed
      sessionStorage.removeItem(key);

      return messages;
    } catch {
      // Corrupt data or unavailable — silent fail
      try { sessionStorage.removeItem(key); } catch { /* ignore */ }
      return [];
    }
  }

  /**
   * Clear any saved conversation snapshot for a workspace.
   */
  clearSavedConversation(workspaceId: string): void {
    try {
      sessionStorage.removeItem(CONVERSATION_STORAGE_PREFIX + workspaceId);
    } catch { /* ignore */ }
  }

  // ==========================================================================
  // RESET & CLEANUP
  // ==========================================================================

  /**
   * Reset state for new session (preserves user preferences)
   */
  resetForNewSession(): void {
    this.state = {
      ...this.state,
      messages: [],
      isLoading: false,
      pendingMessage: null,
      isMenuOpen: false,
      detectedLanguage: null,
      lastActionTime: 0,
      // Preserve: speedMode, useStreaming, isVisible
    };
    this.notifyListeners(['messages', 'isLoading', 'pendingMessage', 'isMenuOpen', 'detectedLanguage']);
  }

  /**
   * Full reset to initial state
   */
  reset(): void {
    const speedMode = this.state.speedMode; // Preserve preference
    const useStreaming = this.state.useStreaming;
    this.state = createInitialState({ speedMode, useStreaming });
    this.notifyListeners([
      'messages', 'isLoading', 'pendingMessage', 'isVisible',
      'isMenuOpen', 'detectedLanguage', 'lastActionTime'
    ]);
  }

  /**
   * Dispose of the state manager, clearing all listeners.
   * Call this when the panel is destroyed to prevent memory leaks.
   */
  dispose(): void {
    this.listeners.clear();
    this.state.messages = [];
    this.state.pendingMessage = null;
  }
}

/**
 * Generate a unique message ID
 */
export function generateMessageId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}
